import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { API } from "../../config";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

interface GalleryItem {
  gallery_id: number;
  title: string | null;
  media_type: string;
  media_url: string | null;
  thumbnail_url: string | null;
  display_order: number;
  record_status_id: number;
}

function getEmbedUrl(url: string): string {
  // Instagram reel: convert to embed URL
  const igMatch = url.match(/instagram\.com\/reel\/([A-Za-z0-9_-]+)/);
  if (igMatch) return `https://www.instagram.com/reel/${igMatch[1]}/embed`;
  // YouTube Shorts: convert to embed
  const ytsMatch = url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]+)/);
  if (ytsMatch) return `https://www.youtube-nocookie.com/embed/${ytsMatch[1]}`;
  // YouTube: convert to embed
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([A-Za-z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube-nocookie.com/embed/${ytMatch[1]}`;
  return url;
}


export const StudentSaySlider = () => {
  const [reels, setReels] = useState<GalleryItem[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const activeReel = activeIndex !== null ? reels[activeIndex] : null;
  const goPrev = () => setActiveIndex((i) => i !== null ? (i === 0 ? reels.length - 1 : i - 1) : null);
  const goNext = () => setActiveIndex((i) => i !== null ? (i === reels.length - 1 ? 0 : i + 1) : null);

  useEffect(() => {
    fetch(`${API}/gallery`)
      .then((r) => r.json())
      .then((data) => {
        const active = (data.items as GalleryItem[])
          .filter((g) => g.record_status_id === 1 && (g.media_type === "reel" || g.media_type === "video(9:16)" || g.media_type === "youtube(9:16)"))
          .sort((a, b) => a.display_order - b.display_order);
        setReels(active);
      })
      .catch(() => {});
  }, []);

  return (
    <section>
      <div className="relative h-[180px] md:h-[220px] flex items-center justify-center bg-violet-100">
        <img src="/images/student-say.png" alt="" aria-hidden="true" width={1161} height={102} loading="lazy" decoding="async" className="absolute inset-0 object-cover md:pt-60 pt-152 w-full" />
        <h2 className="relative mt-330 student-header">
          Student Voices
        </h2>
      </div>

      <div className="student-bg pb-10">
        <div className="relative lg:px-8 max-w-7xl mx-auto px-4 px-55 px-60 sm:px-6 pt-8">
          <div className="overflow-x-hidden swiper studentSwiper">
            <div className="wrapper student-card-hw">
              {reels.length === 0 ? (
                <p className="text-center text-gray-600 py-10">No reels available.</p>
              ) : (
                <Swiper
                  spaceBetween={50}
                  autoplay={{ delay: 5000, disableOnInteraction: false }}
                  pagination={{ el: ".student-pagination", clickable: true }}
                  navigation={true}
                  modules={[Autoplay, Pagination, Navigation]}
                  className="mySwiper"
                  slidesPerView={4}
                  loop={true}
                  watchOverflow={false}
                  breakpoints={{
                    320: { slidesPerView: 1, spaceBetween: 15 },
                    640: { slidesPerView: 1, spaceBetween: 20 },
                    768: { slidesPerView: 2, spaceBetween: 25 },
                    1024: { slidesPerView: 3, spaceBetween: 30 },
                    1280: { slidesPerView: 4, spaceBetween: 30 },
                  }}
                >
                  {reels.map((reel) => (
                    <SwiperSlide key={reel.gallery_id}>
                      <div
                        className="cursor-pointer relative rounded-2xl overflow-hidden mx-auto"
                        style={{ width: "241px", height: "512px" }}
                        onClick={() => setActiveIndex(reels.indexOf(reel))}
                      >
                        {reel.thumbnail_url ? (
                          <img
                            src={reel.thumbnail_url}
                            alt={reel.title ?? "Student reel"}
                            className="w-full h-full object-cover"
                            width={241}
                            height={512}
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"
                            style={{ background: "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)" }}>
                            <svg className="w-16 h-16 text-white/80" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-white/80 rounded-full w-14 h-14 flex items-center justify-center shadow-lg">
                            <svg className="w-6 h-6 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                        {reel.title && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-3">
                            <p className="text-white text-xs font-medium truncate">{reel.title}</p>
                          </div>
                        )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
            <div className="" style={{ transform: "translateX(0%)", padding: "25px 10px" }}>{" "}</div>
          </div>
          <div className="student-pagination mt-4 text-center"></div>
        </div>
      </div>

      {/* Reel Lightbox */}
      {activeReel && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={() => setActiveIndex(null)}
        >
          <div
            className="flex items-center gap-2 sm:gap-4 w-full max-w-[360px] sm:max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Prev */}
            <button
              onClick={goPrev}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-white font-bold text-base shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
              style={{ background: "linear-gradient(135deg, #e7000b 0%, #c40783 100%)", boxShadow: "0 4px 15px rgba(231,0,11,0.5)" }}
              aria-label="Previous"
            ><b>❮</b> </button>

            {/* Video content */}
            <div className="relative flex-1 min-w-0">
              <button
                onClick={() => setActiveIndex(null)}
                className="absolute top-2 right-2 z-20 w-9 h-9 flex items-center justify-center rounded-full text-white font-bold text-base shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
                style={{ background: "linear-gradient(135deg, #e7000b 0%, #c40783 100%)", boxShadow: "0 4px 15px rgba(231,0,11,0.5)" }}
                aria-label="Close"
              >✕</button>

              {activeReel.media_url ? (
                activeReel.media_type === "video(9:16)" ? (
                  <video
                    src={activeReel.media_url}
                    controls
                    autoPlay
                    className="w-full rounded-lg"
                    style={{ aspectRatio: "9/16", maxHeight: "80vh" }}
                  />
                ) : activeReel.media_type === "youtube(9:16)" ? (
                  <iframe
                    src={getEmbedUrl(activeReel.media_url)}
                    className="w-full rounded-lg"
                    style={{ aspectRatio: "9/16", maxHeight: "80vh" }}
                    allowFullScreen
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  />
                ) : (
                  <>
                    <iframe
                      src={getEmbedUrl(activeReel.media_url)}
                      className="w-full rounded-lg"
                      style={{ aspectRatio: "9/16", maxHeight: "80vh" }}
                      allowFullScreen
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                    />
                    <a
                      href={activeReel.media_url}
                      className="block text-center text-white/80 text-sm mt-2 underline hover:text-white"
                    >Watch on Instagram</a>
                  </>
                )
              ) : (
                <div className="text-white text-center py-10">No video available.</div>
              )}
              {activeReel.title && (
                <p className="text-white text-center text-sm mt-2">{activeReel.title}</p>
              )}
            </div>

            {/* Next */}
            <button
              onClick={goNext}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-white font-bold text-base shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
              style={{ background: "linear-gradient(135deg, #e7000b 0%, #c40783 100%)", boxShadow: "0 4px 15px rgba(231,0,11,0.5)" }}
              aria-label="Next"
            ><b>❯</b></button>
          </div>
        </div>
      )}
    </section>
  );
};
